// The shell's client entry point. Every control here is bound by a selector the golden
// masters already record: the comparator treats attributes as a mapping, so a data-* hook
// added inside the app root would be an unexplained difference on every cell
// (doc/spa-substrate-htmx-attributes.md). That constraint is why nothing below reads a
// data attribute, and why the user menu is cloned from a <template> that sits outside the
// compared subtree rather than rendered into it.

import "./emarsys-ribbons"
import { BEFORE_NAVIGATION, captureEvent } from "./utils/posthog"

const MENU_CONTENT = "header__dropdown-content"
const SEARCH_INPUT = "global-search-input"

// Emitted by the layout from `reverse('catalog')` rather than spelled here, so a rename
// of the route moves the nav links and the search submit together.
function catalogUrl() {
  return shellUrls()?.catalog
}

function shellUrls() {
  return document.getElementById("shell-urls")?.dataset
}

function trigger() {
  return document.querySelector(".header__dropdown > .header__icon-link")
}

function openMenu() {
  const dropdown = document.querySelector(".header__dropdown")
  const template = document.getElementById("shell-user-menu")
  if (!dropdown || !template || dropdown.querySelector(`.${MENU_CONTENT}`)) {
    return
  }
  dropdown.append(template.content.cloneNode(true))
  trigger()?.setAttribute("aria-expanded", "true")
  // The first entry is the balance row, which is `sm:!hidden` and so unfocusable on a
  // desktop viewport — focusing it would drop focus on the floor.
  const links = dropdown.querySelectorAll(`.${MENU_CONTENT} a`)
  Array.from(links)
    .find((link) => link.getClientRects().length > 0)
    ?.focus()
}

function closeMenu({ restoreFocus = false } = {}) {
  const menu = document.querySelector(`.${MENU_CONTENT}`)
  if (!menu) {
    return
  }
  menu.remove()
  trigger()?.setAttribute("aria-expanded", "false")
  if (restoreFocus) {
    trigger()?.focus()
  }
}

function menuIsOpen() {
  return Boolean(document.querySelector(`.${MENU_CONTENT}`))
}

// `search-globally` builds the whole filter map and hands it to a full navigation rather
// than a pushState (machtfit/core.cljs), so an empty keyword still travels as `keyword=`.
function searchGlobally(area = shellUrls()?.area ?? "all") {
  const input = document.getElementById(SEARCH_INPUT)
  if (!input) {
    return
  }
  const urls = shellUrls()
  const target =
    urls?.search && urls?.list
      ? areaSearchUrl(input.value, area, urls)
      : catalogSearchUrl(input.value, area)
  if (target) {
    submitSearch(input.value, target)
  }
}

function catalogSearchUrl(term, area) {
  const catalog = catalogUrl()
  return catalog && `${catalog}?${new URLSearchParams({ area, keyword: term })}`
}

// Set for SUBMIT_LOCK_MS after a submit, so a second submit inside that window neither
// navigates nor reports again (DEV-3508). It expires rather than waiting for `pageshow`,
// because a navigation the member stops keeps the page and fires none.
const SUBMIT_LOCK_MS = 1000
let searchSubmitted = false

window.addEventListener("pageshow", () => {
  searchSubmitted = false
})

function areaSearchUrl(term, area, { search, list, magazin }) {
  if (area === "all") {
    return `${search}?${new URLSearchParams({ q: term })}`
  }
  if (area === "magazin") {
    return `${magazin}?${new URLSearchParams({ query: term })}`
  }
  return `${list}?${new URLSearchParams({ keyword: term, area })}`
}

function submitSearch(term, target) {
  if (searchSubmitted) {
    return
  }
  searchSubmitted = true
  setTimeout(() => {
    searchSubmitted = false
  }, SUBMIT_LOCK_MS)
  captureEvent(
    "searched_globally",
    { "search-string": term, "search-engine": "postgres-fts" },
    "searchbar-global",
    BEFORE_NAVIGATION,
  )
  window.top.location.href = target
}

function areaDropdown() {
  return document.querySelector(".search-bar-dropdown")
}

// The dropdown offers the three areas as alternative submits, and the incumbent only
// offers them once there is something to search for. Keyed on `input` rather than the
// incumbent's keyup so a paste or an autofill reveals them too.
function syncAreaDropdown(input) {
  areaDropdown()?.classList.toggle("open", input.value.trim() !== "")
}

document.addEventListener("click", (event) => {
  if (event.target.closest(".header__dropdown > .header__icon-link")) {
    if (menuIsOpen()) {
      closeMenu()
    } else {
      openMenu()
    }
    return
  }

  const areaButton = event.target.closest(".search-bar-dropdown__content")
  if (areaButton) {
    // The area rides on the analytics id the masters already record
    // (`searchbar_global__selection__machtfit`), so the button needs no hook of its own.
    const uiId = areaButton.getAttribute("data-ph-capture-attribute-ui_id") ?? ""
    searchGlobally(uiId.split("__").pop() || "all")
    return
  }

  if (event.target.closest(".global-filter .submit-button")) {
    searchGlobally()
    return
  }

  // Klaro is a third-party modal outside our markup; the helper klaro.js exports is the
  // call startpage/footer.cljs makes too.
  if (event.target.closest("footer .footer__nav-col button.text-left")) {
    window.showKlaroModal?.()
    return
  }

  if (!event.target.closest(".header__dropdown")) {
    closeMenu()
  }
  if (event.target.id === SEARCH_INPUT) {
    syncAreaDropdown(event.target)
  } else if (!event.target.closest(".global-filter")) {
    areaDropdown()?.classList.remove("open")
  }
})

document.addEventListener("keydown", (event) => {
  if (event.key === "Escape") {
    // The incumbent's own Escape handler has never fired — its guard is a three-argument
    // `=` that cannot be true (doc/spa-interaction.md, DEV-3702). Closing on Escape here
    // is deliberate and leaves no trace in the recorded closed DOM.
    closeMenu({ restoreFocus: true })
    areaDropdown()?.classList.remove("open")
    return
  }
  if (event.key === "Enter" && event.target.id === SEARCH_INPUT) {
    event.preventDefault()
    if (!event.repeat) {
      searchGlobally()
    }
  }
})

document.addEventListener("input", (event) => {
  if (event.target.id === SEARCH_INPUT) {
    syncAreaDropdown(event.target)
  }
})

function acknowledgeNotification(dialog) {
  const form = dialog.querySelector("form")
  // keepalive lets the request outlive a link in the pop-up navigating the page away.
  fetch(form.action, { method: "POST", body: new FormData(form), keepalive: true }).catch(() => {})
}

const notification = document.getElementById("shell-notification")
if (notification) {
  notification.addEventListener("close", () => acknowledgeNotification(notification))
  notification.addEventListener("click", (event) => {
    if (event.target.closest(".notification__content a")) {
      acknowledgeNotification(notification)
    }
  })
  // This bundle is deferred, so its DOMContentLoaded listener runs after klaro's setup.
  document.addEventListener("DOMContentLoaded", () => {
    const open = () => notification.showModal()
    window.afterKlaroConsent ? window.afterKlaroConsent(open) : open()
  })
}
